"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GardenFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    value: "item-1",
    question: "How do I reserve a garden plot?",
    answer: "Plot reservations open every spring. Members can request a 4x8 raised bed through our online portal. We prioritize returning members, but new spots open up annually based on availability. Check our membership page for the next registration window.",
    spec: "AVAILABILITY — SEASONAL"
  }, {
    value: "item-2",
    question: "Are organic practices required?",
    answer: "Yes. To maintain the health of our shared soil and local pollinators, we strictly prohibit synthetic pesticides and chemical fertilizers. We provide organic compost and natural pest management resources to all our members.",
    spec: "POLICY — 100% ORGANIC"
  }, {
    value: "item-3",
    question: "What is the seed-swapping event?",
    answer: "Our monthly seed-swap is a community gathering where members trade heirloom seeds, share propagation tips, and discuss what's thriving in their plots. It's a great way to diversify your garden without extra costs.",
    spec: "EVENT — MONTHLY GATHERING"
  }, {
    value: "item-4",
    question: "Can I volunteer without a plot?",
    answer: "Absolutely! We welcome community volunteers to help with greenhouse maintenance, composting, and general site upkeep. It's a wonderful way to learn gardening skills and meet neighbors even if you don't have a dedicated plot.",
    spec: "VOLUNTEER — ALWAYS OPEN"
  }, {
    value: "item-5",
    question: "How does the composting system work?",
    answer: "We maintain a three-bin hot composting system. Members are encouraged to bring kitchen scraps, which we process into nutrient-rich soil amendments. We provide a guide on what can and cannot be composted at our site.",
    spec: "SYSTEM — THREE-BIN HOT"
  }, {
    value: "item-6",
    question: "Are there workshops for beginners?",
    answer: "We host seasonal workshops covering everything from soil preparation to winterizing your garden. These sessions are led by experienced local growers and are free for all active club members.",
    spec: "WORKSHOPS — SEASONAL"
  }, {
    value: "item-7",
    question: "What happens to the harvest?",
    answer: "Individual plot harvests belong to the member. However, we also maintain communal rows where produce is donated to local food banks. We encourage members to share their surplus with neighbors.",
    spec: "COMMUNITY — SHARED HARVEST"
  }, {
    value: "item-8",
    question: "Is there a membership fee?",
    answer: "Yes, a modest annual fee covers water access, tool maintenance, and communal supplies. We offer sliding-scale options to ensure that our community garden remains accessible to all neighborhood residents.",
    spec: "FEE — SLIDING SCALE"
  }];
  return <section data-section-id="28" id="garden-faq" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 lg:px-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-xs tracking-widest text-primary uppercase mb-8">
                S/02 — FAQ
              </p>
              <h2 className="font-default text-5xl md:text-6xl lg:text-[64px] leading-[0.95] font-bold uppercase text-foreground tracking-tight mb-6">
                Growing
                <br />
                Together
              </h2>
              <p className="text-base md:text-[17px] text-muted-foreground leading-relaxed mb-12 max-w-md">
                Answers to common questions about our community garden, plot rules, and how to get involved with Eco Grow.
              </p>

              {}
              <div className="border-t border-border pt-8">
                <div className="flex items-center gap-4 mb-5">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_coordinator_sarah.png" alt="Sarah, Garden Coordinator" loading="lazy" decoding="async" className="w-[60px] h-[60px] rounded-lg object-cover shrink-0" />
                  <div>
                    <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase mb-1">
                      ON SITE
                    </p>
                    <p className="font-default text-sm font-bold uppercase tracking-wide text-foreground leading-tight">
                      Sarah Miller — Garden Coordinator,
                      <br />
                      Master Gardener
                    </p>
                  </div>
                </div>
                <Link to="/JoinUsPage" className="inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors">
                  Contact Coordinator
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.value} value={faq.value} data-index={i} className="border-b border-border first:border-t border-t-0">
                  <AccordionTrigger className="group py-8 hover:no-underline rounded-md [&>svg]:hidden text-left items-center">
                    <span className="font-default text-lg md:text-[19px] font-bold uppercase tracking-wide text-foreground pr-4 leading-snug">
                      {faq.question}
                    </span>
                    <Plus className="size-6 shrink-0 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45" aria-hidden="true" />
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-10">
                    <p className="text-base text-muted-foreground leading-[1.7] mb-4">
                      {faq.answer}
                    </p>
                    <p className="font-mono text-[11px] tracking-widest uppercase text-primary inline-block border border-border px-2.5 py-1 rounded-md">
                      {faq.spec}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}